import React, {Component, createRef, RefObject, Fragment} from 'react';
import {Button} from '@material-ui/core';
import Modal from 'components/Modal';
import {CurrencyAmount} from 'util/currency';
import PaymentMethodField from 'components/forms/PaymentMethodField';
import CheckBoxField from 'components/forms/CheckBoxField';
import {handleError} from 'util/errors';
import Note from '../Note';
import {MastercardIcon, SecurityIcon, VisaIcon} from 'components/Icon';
import ProgressSpinner from 'components/ProgressSpinner';
import {ValidationGroup, Validators} from 'util/validation';
import Form, {FormStatus} from 'components/forms/Form';
import './index.scss';
import {generateDraftBookingOccupationalLicense} from 'services/bookings/drafts';
import {DraftBooking} from 'services/entities/booking';
import NextRouteURLs from 'NextRouteURLs';

interface PaymentModalState {
  status: FormStatus;
  licenseURL: string|null;
}

interface PaymentModalProps {
  open: boolean;
  booking: DraftBooking;
  price: CurrencyAmount;
  onCancelled: ()=>void;
  onSuccess: (paymentMethodID: string)=>Promise<any>;
}

interface PaymentModalModel {
  paymentMethod: boolean;
  termsAgreed: boolean;
  licenseSigned: boolean;
}

export default class PaymentModal extends Component<PaymentModalProps, PaymentModalState> {
  private readonly paymentMethod: RefObject<PaymentMethodField> = createRef();
  private readonly validation: ValidationGroup;
  private readonly model: PaymentModalModel;

  state = {
    status: FormStatus.None,
    licenseURL: null
  };

  constructor(props: PaymentModalProps) {
    super(props);
    this.model = {
      paymentMethod: false,
      termsAgreed: false,
      licenseSigned: false
    };
    this.validation = new ValidationGroup(this.model);
    this.validation.addField("paymentMethod", { required: Validators.required });
    this.validation.addField("termsAgreed", { required: Validators.required });
    this.validation.addField("licenseSigned", { required: Validators.required });
  }

  componentDidMount() {
    if(this.props.open) {
      this.loadOccupationalLicenseURL();
    }
  }

  componentWillUnmount() {
    const licenseURL = this.state.licenseURL;
    if(licenseURL) {
      URL.revokeObjectURL(licenseURL);
    }
  }

  componentDidUpdate(prevProps: Readonly<PaymentModalProps>, prevState: Readonly<PaymentModalState>, snapshot?: any) {
    if(prevProps.open !== this.props.open && this.props.open) {
      this.loadOccupationalLicenseURL();
    }
  }

  loadOccupationalLicenseURL() {
    generateDraftBookingOccupationalLicense(this.props.booking, this.props.price).then(
      license => this.setState({licenseURL: URL.createObjectURL(license)}),
      ex => handleError('Failed generating occupational license', ex)
    );
  }

  onClosing = () => {
    if(this.state.status === FormStatus.Submitting) {
      return;
    }
    this.props.onCancelled();
  };

  onSubmit = () => {
    const paymentMethod: PaymentMethodField|null = this.paymentMethod.current;
    if(!paymentMethod) {
      handleError("Payment method not initialized");
      return;
    }
    this.setState({ status: FormStatus.Submitting });
    paymentMethod.setupPaymentMethod().then(
      paymentMethodID => {
        this.props.onSuccess(paymentMethodID).then(
          () => this.setState({status: FormStatus.Success}),
          () => this.setState({status: FormStatus.Failure})
        );
      },
      err => {
        handleError('Payment failed', err);
        this.setState({ status: FormStatus.Failure });
      }
    );
  };

  render() {
    const price: CurrencyAmount = this.props.price;
    const disabled = this.state.status === FormStatus.Submitting || this.state.status === FormStatus.Success;
    const loading = this.state.status === FormStatus.Submitting;
    const licenseURL = this.state.licenseURL;
    return <Modal className="payment-modal" open={this.props.open} onClosing={this.onClosing}>
      <Form onSubmit={this.onSubmit} validationGroup={this.validation}>
        <section className="header">
          <h2>My booking</h2>
          <p className="subtitle">Confirm my booking</p>
          <p className="price">
            <span className="label">Total cost</span>
            <span className="value">{ price.format('$0.00') } VAT Incl.</span>
          </p>
        </section>
        <section className="payment-method">
          <h3>Payment method</h3>
          <PaymentMethodField
            ref={this.paymentMethod}
            disabled={disabled}
            name="paymentMethod"
            model={this.model}
            validationGroup={this.validation}
            validationMessages={{required: 'Card information is incomplete'}}
          />
        </section>
        <section className="license">
          <h3>License</h3>
          <CheckBoxField
            disabled={disabled}
            name="termsAgreed"
            model={this.model}
            validationGroup={this.validation}
            validationMessages={{required: 'You must agree to the Terms & Conditions' }}
            label={<Fragment>I agree to the <a href={NextRouteURLs.TERMS_AND_CONDITIONS}>Terms & Conditions</a> and <a href={NextRouteURLs.PRIVACY}>Privacy Policy</a></Fragment>} />
          <CheckBoxField
            disabled={disabled}
            name="licenseSigned"
            model={this.model}
            validationGroup={this.validation}
            validationMessages={{required: 'You must sign the Occupation License' }}
            label={<Fragment>I sign the { licenseURL ? <a href={licenseURL} target="_blank" rel="noopener noreferrer">Occupational License</a> : 'Occupational License' } for this booking</Fragment>} />
          <Note>
            A member of the Sook team will be in touch shortly to discuss your booking, and clarify any details.
          </Note>
        </section>
        <section className="actions">
          { !loading && <Button disabled={disabled} type="submit">Confirm booking</Button> }
          { loading && <ProgressSpinner/> }
        </section>
      </Form>
      <section className="footer">
        <p><SecurityIcon/> This payment is secured by Stripe</p>
        <ul className="accepted-payments">
          <li><VisaIcon/></li>
          <li><MastercardIcon/></li>
        </ul>
      </section>
    </Modal>;
  }
}